import { useState, useEffect, useCallback, useRef } from 'react';

export type ConnectionQuality = 'excellent' | 'good' | 'slow' | 'very-slow' | 'offline';

interface ConnectionInfo {
  quality: ConnectionQuality;
  effectiveType: string | null;
  downlink: number | null;
  rtt: number | null;
  saveData: boolean;
  isOnline: boolean;
}

interface NetworkInformation extends EventTarget {
  effectiveType: '2g' | '3g' | '4g' | 'slow-2g';
  downlink: number;
  rtt: number;
  saveData: boolean;
  addEventListener(type: 'change', listener: () => void): void;
  removeEventListener(type: 'change', listener: () => void): void;
}

declare global {
  interface Navigator {
    connection?: NetworkInformation;
    mozConnection?: NetworkInformation;
    webkitConnection?: NetworkInformation;
  }
}

// Thresholds for connection quality
const QUALITY_THRESHOLDS = {
  rtt: {
    excellent: 100,
    good: 300,
    slow: 600,
  },
  downlink: {
    excellent: 10,
    good: 2,
    slow: 0.5,
  },
};

function getQualityFromMetrics(rtt: number | null, downlink: number | null, effectiveType: string | null): ConnectionQuality {
  // Use effectiveType as primary indicator if available
  if (effectiveType) {
    switch (effectiveType) {
      case 'slow-2g':
        return 'very-slow';
      case '2g':
        return 'slow';
      case '3g':
        return 'good';
      case '4g':
        return 'excellent';
    }
  }

  // Fall back to RTT and downlink analysis
  if (rtt !== null && downlink !== null) {
    if (rtt <= QUALITY_THRESHOLDS.rtt.excellent && downlink >= QUALITY_THRESHOLDS.downlink.excellent) {
      return 'excellent';
    }
    if (rtt <= QUALITY_THRESHOLDS.rtt.good && downlink >= QUALITY_THRESHOLDS.downlink.good) {
      return 'good';
    }
    if (rtt <= QUALITY_THRESHOLDS.rtt.slow && downlink >= QUALITY_THRESHOLDS.downlink.slow) {
      return 'slow';
    }
    return 'very-slow';
  }

  // Default to good if we can't determine
  return 'good';
}

// Singleton state for global access
let globalConnectionInfo: ConnectionInfo = {
  quality: 'good',
  effectiveType: null,
  downlink: null,
  rtt: null,
  saveData: false,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
};

const connectionListeners = new Set<(info: ConnectionInfo) => void>();

function notifyListeners() {
  connectionListeners.forEach((listener) => listener(globalConnectionInfo));
}

function updateConnectionInfo() {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const isOnline = navigator.onLine;

  if (!isOnline) {
    globalConnectionInfo = {
      ...globalConnectionInfo,
      quality: 'offline',
      isOnline: false,
    };
  } else if (connection) {
    const quality = getQualityFromMetrics(connection.rtt, connection.downlink, connection.effectiveType);
    globalConnectionInfo = {
      quality,
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData || false,
      isOnline: true,
    };
  } else {
    globalConnectionInfo = {
      ...globalConnectionInfo,
      isOnline: true,
      quality: 'good', // Assume good if we can't measure
    };
  }

  notifyListeners();
}

// Initialize listeners once
if (typeof window !== 'undefined') {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  window.addEventListener('online', updateConnectionInfo);
  window.addEventListener('offline', updateConnectionInfo);

  if (connection) {
    connection.addEventListener('change', updateConnectionInfo);
  }

  // Initial update
  updateConnectionInfo();
}

export function getConnectionInfo(): ConnectionInfo {
  return globalConnectionInfo;
}

export function subscribeToConnectionQuality(callback: (info: ConnectionInfo) => void): () => void {
  connectionListeners.add(callback);
  return () => connectionListeners.delete(callback);
}

export function useConnectionQuality() {
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo>(globalConnectionInfo);
  const lastPingTime = useRef<number | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Subscribe to connection changes
  useEffect(() => {
    const unsubscribe = subscribeToConnectionQuality(setConnectionInfo);
    return unsubscribe;
  }, []);

  // Periodic latency check using fetch
  const measureLatency = useCallback(async () => {
    if (!navigator.onLine) return;

    const start = performance.now();
    try {
      // Use a small request to measure latency
      await fetch('/favicon.png', { 
        method: 'HEAD', 
        cache: 'no-store',
        mode: 'no-cors'
      });
      const latency = performance.now() - start;
      lastPingTime.current = latency;

      // Update quality based on measured latency if Network API not available
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (!connection) {
        let measuredQuality: ConnectionQuality = 'good';
        if (latency < 100) measuredQuality = 'excellent';
        else if (latency < 300) measuredQuality = 'good';
        else if (latency < 600) measuredQuality = 'slow';
        else measuredQuality = 'very-slow';

        globalConnectionInfo = {
          ...globalConnectionInfo,
          quality: measuredQuality,
          rtt: Math.round(latency),
        };
        notifyListeners();
      }
    } catch {
      // Fetch failed, might be offline or slow
      if (navigator.onLine) {
        globalConnectionInfo = {
          ...globalConnectionInfo,
          quality: 'very-slow',
        };
        notifyListeners();
      }
    }
  }, []);

  // Set up periodic latency measurement (less frequent on mobile)
  useEffect(() => {
    // Delay initial measurement to avoid competing with page load
    const initialTimeout = setTimeout(measureLatency, 5000);

    // Measure every 60 seconds to reduce overhead
    pingIntervalRef.current = setInterval(measureLatency, 60000);

    return () => {
      clearTimeout(initialTimeout);
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [measureLatency]);

  // Helper to check if we should reduce data usage
  const shouldReduceData = connectionInfo.saveData || 
    connectionInfo.quality === 'slow' || 
    connectionInfo.quality === 'very-slow';

  // Helper to check if requests should be deferred
  const shouldDeferRequests = connectionInfo.quality === 'very-slow' || !connectionInfo.isOnline;

  // Get adaptive timeout based on connection quality
  const getAdaptiveTimeout = useCallback((baseTimeout: number = 10000): number => {
    switch (connectionInfo.quality) {
      case 'excellent':
        return baseTimeout;
      case 'good':
        return baseTimeout * 1.5;
      case 'slow':
        return baseTimeout * 2;
      case 'very-slow':
        return baseTimeout * 3;
      case 'offline':
        return baseTimeout;
      default:
        return baseTimeout;
    }
  }, [connectionInfo.quality]);

  // Get adaptive retry count
  const getAdaptiveRetryCount = useCallback((baseRetries: number = 3): number => {
    switch (connectionInfo.quality) {
      case 'excellent':
      case 'good':
        return baseRetries;
      case 'slow':
        return baseRetries + 1;
      case 'very-slow':
        return baseRetries + 2;
      case 'offline':
        return 0;
      default:
        return baseRetries;
    }
  }, [connectionInfo.quality]);

  return {
    ...connectionInfo,
    shouldReduceData,
    shouldDeferRequests,
    getAdaptiveTimeout,
    getAdaptiveRetryCount,
    measureLatency,
    lastPingTime: lastPingTime.current,
  };
}
